// Location check helpers shared by Group settings and the time tracker.
// formatDistance mirrors formatDistance in backend/create-time-log/location-check.mjs;
// tests/location-check.test.js and tests/backend/time-log-location-check.test.mjs pin the same cases.

export const IMPERIAL_COUNTRIES = ['US', 'LR', 'MM']
export const RADIUS_MIN_M = 100
export const RADIUS_MAX_M = 5000
export const RADIUS_DEFAULT_M = 150
const METERS_PER_FOOT = 0.3048

export interface DeviceLocation {
  location: { latitude: number; longitude: number; accuracy: number } | null
  location_error: null | 'denied' | 'unavailable' | 'timeout' | 'unsupported'
}

export function isImperial(countryCode?: string | null): boolean {
  return IMPERIAL_COUNTRIES.includes(String(countryCode || '').toUpperCase())
}

export function formatDistance(meters: number | null | undefined, countryCode?: string | null): string {
  if (meters === null || meters === undefined || !Number.isFinite(Number(meters))) return ''
  const m = Number(meters)
  if (isImperial(countryCode)) {
    const feet = m / METERS_PER_FOOT
    if (feet < 1000) return `${Math.round(feet / 10) * 10} ft`
    return `${(m / 1609.344).toFixed(1)} mi`
  }
  if (m < 1000) return `${Math.round(m / 10) * 10} m`
  return `${(m / 1000).toFixed(1)} km`
}

// Radii are stored in whole meters, so feet round to the nearest 10: a value
// typed in tens of feet then reads back exactly (one meter is about 3 ft).
export function metersToInputUnits(meters: number, countryCode?: string | null): number {
  return isImperial(countryCode) ? Math.round(meters / METERS_PER_FOOT / 10) * 10 : Math.round(meters)
}

export function inputUnitsToMeters(value: number, countryCode?: string | null): number {
  return isImperial(countryCode) ? Math.round(value * METERS_PER_FOOT) : Math.round(value)
}

export function distanceUnitLabel(countryCode?: string | null): string {
  return isImperial(countryCode) ? 'ft' : 'm'
}

export function groupCountry(): string {
  try {
    return JSON.parse(localStorage.getItem('group') || '{}').country || ''
  } catch (e) {
    return ''
  }
}

// One location fix from the browser. Never rejects.
export async function getDeviceLocation(): Promise<DeviceLocation> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { location: null, location_error: 'unsupported' }
  }
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
      if (status.state === 'denied') return { location: null, location_error: 'denied' }
    }
  } catch (e) {
    // Permissions API missing or unsupported for geolocation; fall through to the request.
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        location: { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy },
        location_error: null,
      }),
      (err) => resolve({
        location: null,
        location_error: err.code === 1 ? 'denied' : err.code === 3 ? 'timeout' : 'unavailable',
      }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  })
}

export type LocationResult = 'passed' | 'outside' | 'unavailable' | 'manual' | 'not_required' | null

export function locationChipLabel(result: LocationResult): string {
  if (result === 'outside') return 'Off site'
  if (result === 'unavailable') return 'No location'
  if (result === 'manual') return 'Manual'
  return ''
}

export function locationResultLabel(result: LocationResult): string {
  if (result === 'passed') return 'On site'
  if (result === 'not_required') return 'Not required'
  return locationChipLabel(result)
}

// Copy for a rejected start (code location_blocked) or a warning (code location_note_required).
export function describeLocationCheck(body: { code?: string; reason?: string; distance_text?: string }): { title: string; message: string } {
  const blocked = body.code === 'location_blocked'
  if (body.reason === 'outside') {
    return blocked
      ? { title: 'Start on site', message: `You're ${body.distance_text} from the venue. This category can only be started on site.` }
      : { title: 'You appear to be off site', message: `You're ${body.distance_text} from the venue. Add a note to start the timer. The entry is highlighted in the time logs.` }
  }
  if (body.reason === 'denied') {
    return blocked
      ? { title: 'Location needed', message: 'This category needs your location to start. Allow location access for this site in your browser settings, then try again.' }
      : { title: 'Location is off', message: 'Location access is off for this site. Add a note to start the timer. The entry is highlighted in the time logs.' }
  }
  if (body.reason === 'imprecise') {
    return blocked
      ? { title: 'Location not precise enough', message: "Your location isn't precise enough to confirm you're on site. Start this timer from your phone." }
      : { title: 'Location not confirmed', message: "Your location isn't precise enough to confirm you're on site. Add a note to start the timer. The entry is highlighted in the time logs." }
  }
  return blocked
    ? { title: 'Location not found', message: "Your location couldn't be found. Try again, or start this timer from your phone." }
    : { title: 'Location not confirmed', message: "Your location couldn't be found. Add a note to start the timer. The entry is highlighted in the time logs." }
}

// Categories drawer (OTC-2926 Phase 14): status chip and mode choices.
export const LOCATION_MODE_COLORS: Record<string, string> = {
  block: '#fd5d93',
  warning: '#ff8d72',
  off: 'rgba(125, 125, 125, 1)',
}

export const LOCATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off', description: 'Staff start timers from anywhere.' },
  { value: 'warning', label: 'Warning', description: 'Staff add a note, and the timer starts.' },
  { value: 'block', label: 'Block', description: "The timer won't start. Managers can still add entries by hand." },
]

// The radius shows in the unit staff type it in (ft or m), not formatDistance's
// mi or km, so a category saved at 1,000 ft reads 1,000 ft.
export function locationStatusChip(mode: string | null | undefined, radiusM: number | null | undefined, countryCode?: string | null): { label: string; color: string } {
  const distance = `${metersToInputUnits(Number(radiusM) || RADIUS_DEFAULT_M, countryCode).toLocaleString('en-US')} ${distanceUnitLabel(countryCode)}`
  if (mode === 'block') return { label: `Block · ${distance}`, color: LOCATION_MODE_COLORS.block }
  if (mode === 'warning') return { label: `Warning · ${distance}`, color: LOCATION_MODE_COLORS.warning }
  return { label: 'No location check', color: LOCATION_MODE_COLORS.off }
}
